import { closeSDKAbTest, isFromCutout } from './../constants/index';
import * as native from '@/utils/native';
import {
  redEnvelopePromotionInfoAtom,
  resultModalTitleAtom,
  visibleResultAtom,
} from './../store/common';
import { rebateCodeAtom, teamInfoAtom, vipInfoAtom, giftLevelAtom } from './../store/user';
import {
  subEntranceBizCodeAtom,
  subscriptionEntranceEntriesAtom,
  apiSelectedProductIdAtom,
} from './../store/sub';
import {
  productsAtom,
  loadingAtom,
  qrCodeUrlAtom,
  curProductAtom,
  seatsAtom,
  activeIndexAtom,
  payProductInfoAtom,
  subAllProductsAtom,
  proAllProductsAtom,
} from './../store/product';
import * as Sentry from '@sentry/react';
import {
  isTeamSubscription,
  SubscribeType,
  subscribeType,
  getAppIdEnum,
  purchaseScene,
  PurchaseScene,
  isFromAiLogo,
  isFromLivePPT,
  isOnlyShowScanUI,
} from '@/constants';
import {
  SubscribeEnv,
  Subscribe,
  SubscriptionPlatform,
  MeiDou,
  ClientInfo,
  AccountType,
  GroupEntranceScene,
  SubscribeGroupAPIPaths,
  TradePurchaseScene,
  ProductType,
  GroupProduct,
  SubscriptionPeriod,
  SubscribeAPIPaths,
  MeiDouAPIPaths,
  TradeParams,
} from '@meitu/subscribe-core';
import { Product } from '@/types/product';
import { getSkuType, getUrlParam, jsonToHump, showToast } from '.';
import { getDefaultStore } from 'jotai';
import { curTabAtom, payUrlAtom } from '@/store';
import { PlatfromType, TabType, isStandardVipLevel, PromotionType } from '@/constants/subscription';
import {
  checkIsDefaultSecondTierMeidou,
  checkIsNewUI,
  getAbInfo,
  reportABCodeEnterTest,
} from './abtest';
import { resolveTeamSubscriptionEntrance } from './subscriptionEntrance';
import { getIncomeSource, getVipTag, isAppStorePay, isMobileH5 } from './vip';
import { getTrackSubscribeDataParams } from './transferId';
import { getMeidouCount, getUserInfo } from './user';
import { track } from './common';
import { fetchProductListOfMac } from '@/services/subscription';
import { getLocaleStore } from '@/i18n/localeStore';
import { getLanguage } from '@/i18n/constants';

const store = getDefaultStore();
const { $t } = getLocaleStore();

let pollTimer: any;
let subInstance: Subscribe | MeiDou;
let transferId: string = '';
let isFirstSetActiveIndex = true;

const getSdkOptions = () => {
  // 美图大账号token
  const accessToken = window.localStorage.getItem('access_token') || '';

  let env: any;
  switch (process.env.REACT_APP_ENV) {
    case 'pre':
      env = SubscribeEnv.Pre;
      break;
    case 'beta':
      env = SubscribeEnv.Beta;
      break;
    default:
      env = SubscribeEnv.Release;
  }

  const abCodes = closeSDKAbTest
    ? ''
    : (getAbInfo().ab_codes || [])
        .map((item: { code: string; count: number }) => {
          return item.code;
        })
        .join(',');

  const appId = getAppIdEnum().MEITUSHEJISHI;

  const clientInfo: ClientInfo = {
    appId,
    sdkVersion: '1.0.0',
    platform: SubscriptionPlatform.PCWeb,
    abCodes,
    language: getLanguage(),
  };

  if (isTeamSubscription) {
    clientInfo.oid = getUrlParam('oid');
    clientInfo.groupId = getUrlParam('groupId');
  }

  const requestOptions = {
    onResponse: (response: Response) => {
      const { headers, url } = response;
      // 目前美豆接口中台未支持下发hit_ab_code，所以上报不了埋点进入事件（会员商品类型已支持了）
      const abCode = headers.get('hit_ab_code');

      // 判断是否是获取商品列表接口
      const isProductsPath =
        url.indexOf('/h5/product/v2/products_by_biz_code.json') > -1 ||
        url.indexOf('/h5/meidou/entrance/v2/products_by_biz_code.json') > -1;

      if (!abCode || !isProductsPath) return;

      reportABCodeEnterTest(+abCode);
    },
  };
  return {
    accessToken,
    clientInfo,
    env,
    requestOptions,
  };
};

/**
 * 初始化会员SDK
 */
export const initSubscribeSdk = () => {
  const options = getSdkOptions();

  // 实例化web sdk
  const instance = new Subscribe(options);

  return instance;
};

/**
 * 初始化美豆SDK
 * @returns
 */
export const initMeidouSdk = () => {
  const options = getSdkOptions();

  // 实例化美豆 sdk
  const instance = new MeiDou(options);

  return instance;
};

/**
 * 美豆商品列表
 */
const getMeidouProducts = async () => {
  subInstance = initMeidouSdk();

  try {
    const entranceBizCode = store.get(subEntranceBizCodeAtom);
    const { productList } = await (subInstance as MeiDou).fetchMeiDouEntrance({
      entranceBizCode,
    });
    store.set(productsAtom, productList || []);
  } catch (e) {
    console.log(e, 'fetch meidou entrance error=========');
  }
};

/**
 * 根据指定 tab 类型获取对应的 entranceBizCode
 */
const getEntranceBizCodeForTab = (tabType: TabType): string => {
  const entries = store.get(subscriptionEntranceEntriesAtom);
  const rebateCode = store.get(rebateCodeAtom);
  if (!entries) return '';

  if (tabType === TabType.MEIDOU) return entries.meidou;
  if (tabType === TabType.CUTOUT) return entries.cutout;
  if (tabType === TabType.PRO_SUB) {
    const giftLevel = store.get(giftLevelAtom);
    const key = `svip_${giftLevel}` as keyof typeof entries;
    return entries[key] || '';
  }

  // 邀请页打开的优惠购买
  if (
    getIncomeSource() === 'MTXX-WEB' &&
    getUrlParam('isFromInvitePage') === '1' &&
    !isTeamSubscription &&
    tabType === TabType.SUB &&
    rebateCode
  ) {
    return 'commission_discounts';
  }

  // PPT/LOGO/PC 站点
  if (
    !isTeamSubscription &&
    (isFromLivePPT || isFromAiLogo) &&
    getIncomeSource() === 'MTXX-WEB' &&
    tabType === TabType.SUB
  ) {
    return entries.subscription_ppt_logo || '';
  }

  if (isTeamSubscription && tabType === TabType.SUB) {
    return resolveTeamSubscriptionEntrance(entries, store.get(teamInfoAtom));
  }

  return entries.subscription;
};

/**
 * 根据指定 entranceBizCode 获取会员商品列表（不写入 productsAtom）
 */
const fetchSubProductsByBizCode = async (entranceBizCode: string): Promise<Product[]> => {
  subInstance = initSubscribeSdk();
  if (!subInstance || !entranceBizCode) return [];

  const params: any = { entranceBizCode };

  const res = await subInstance.fetchEntrance(params);
  const list: Product[] = res.productList;

  return list || [];
};

/**
 * 会员商品列表
 * @returns
 */
const getSubProducts = async () => {
  subInstance = initSubscribeSdk();

  const entranceBizCode = store.get(subEntranceBizCodeAtom);
  const curTab = store.get(curTabAtom);

  if (!subInstance || !entranceBizCode) return [];

  const params: any = {
    entranceBizCode,
  };

  if (isTeamSubscription) {
    params.accountId = getUrlParam('groupId');
    params.accountType = AccountType.GroupId;
    params.entranceScene =
      purchaseScene === PurchaseScene.EXTRA ? GroupEntranceScene.Addons : GroupEntranceScene.Normal;
    params.appId = getAppIdEnum().MEITUSHEJISHI;
  }

  const fetchGroupProducts = async () => {
    const res = await subInstance.request(
      `${process.env.REACT_APP_API_SUB_HOST}${SubscribeGroupAPIPaths.products}`,
      { params }
    );

    return res?.data || [];
  };

  try {
    const res = isTeamSubscription
      ? await fetchGroupProducts()
      : await subInstance.fetchEntrance(params);

    let list: Product[] = isTeamSubscription ? res : res?.productList || [];

    const vipInfo = store.get(vipInfoAtom);
    // 高级会员看标准会员入口时默认只展示续期商品；扫码弹窗（isOnlyShowScanUI=1）需保留非续期商品，不按此规则过滤
    if (
      getIncomeSource() === 'MTXX-WEB' &&
      curTab === TabType.SUB &&
      getVipTag(vipInfo).proVip &&
      !isOnlyShowScanUI
    ) {
      list = list.filter((item) => item.productType === ProductType.RenewedSubscription);
    }

    store.set(productsAtom, list || []);

    !isTeamSubscription && store.set(redEnvelopePromotionInfoAtom, res?.redEnvelopePromotion);
  } catch (e) {
    console.log(e, 'fetch sub entrance error=========');
  }
};

/**
 * 获取mac商店的商品列表
 */
export const getProductMacList = async () => {
  const entranceBizCode = store.get(subEntranceBizCodeAtom);
  const countryCode = await native.getCountryCode();
  const params = {
    app_id: getAppIdEnum().MEITUSHEJISHI,
    access_token: localStorage.getItem('access_token') || '',
    entrance_biz_code: entranceBizCode,
    platform: PlatfromType.IOS,
    country_code: countryCode,
    expected_language: getLanguage(),
  };

  const res = await fetchProductListOfMac(params);

  if (res?.data?.code === 0 && res?.data?.data) {
    const list = res.data.data;

    jsonToHump(list);

    list.forEach((item: any) => {
      item.checkBox = {};
      if (item.buttonSubtitle) {
        item.checkBox.explain = item.buttonSubtitle;
      }
      item.appId = item.appIdString;
      item.productId = item.productIdString;
    });

    store.set(productsAtom, list as any);
  }
};

/**
 * 获取商品组列表数据
 * @param isNewUI 是否新版订阅UI，新版非美豆场景并行获取标准+高级会员商品
 */
export const getProducts = async (isNewUI = false) => {
  const curTab = store.get(curTabAtom);
  if (curTab === TabType.MEIDOU) {
    await getMeidouProducts();

    // 美豆商品：须结束 loading，否则 meidou-products 内 handleSelect 会因 loading 一直拦截点击
    store.set(loadingAtom, false);
  } else if (isNewUI) {
    // 新版UI：并行获取标准会员和高级会员商品
    const subBizCode = getEntranceBizCodeForTab(TabType.SUB);
    const proBizCode = getEntranceBizCodeForTab(TabType.PRO_SUB);

    const [subList, proList] = await Promise.all([
      fetchSubProductsByBizCode(subBizCode).catch(() => [] as Product[]),
      proBizCode
        ? fetchSubProductsByBizCode(proBizCode).catch(() => [] as Product[])
        : Promise.resolve([] as Product[]),
    ]);

    store.set(subAllProductsAtom, subList);
    store.set(proAllProductsAtom, proList);
    store.set(loadingAtom, false);
  } else {
    // 旧版UI：仅获取当前 tab 对应的商品
    await getSubProducts();
  }
};

/**
 * 新版订阅页：按当前 giftLevel 对应的 svip_N 入口仅重新拉取高级会员商品组
 */
export const refreshProProductsForNewUI = async () => {
  const proBizCode = getEntranceBizCodeForTab(TabType.PRO_SUB);
  if (!proBizCode) {
    store.set(proAllProductsAtom, []);
    return;
  }
  const proList = await fetchSubProductsByBizCode(proBizCode).catch(() => [] as Product[]);
  store.set(proAllProductsAtom, proList);
};

export const getTransferId = () => {
  return transferId;
};

/**
 * 轮询交易会话的支付结果
 * @param productInfo 轮询交易会话的支付结果
 */
const pollTradeProgress = (productInfo: Product) => {
  const curTab = store.get(curTabAtom);
  pollTimer = window.setInterval(async () => {
    try {
      const res = await subInstance
        ?.productTradeProgress({
          tradeSessionId: productInfo.tradeSessionId, // 需要轮询商品列表交易进度时，必传
        })
        .catch((error: any) => {
          // 黑灰产策略触发异常提示
          showToast(
            error?.message || error || '',
            document.querySelector('.contentBox') as HTMLDivElement
          );
          clearInterval(pollTimer);
          setTimeout(() => {
            store.set(activeIndexAtom, -1);
            getProducts();
          }, 3000);
        });

      if (res?.havePaidTrade) {
        // 等待2秒，确保用户权益信息已更新
        await new Promise((resolve) => setTimeout(resolve, 2000));
        await getUserInfo();
        store.set(
          resultModalTitleAtom,
          $t((v) => v.Title_ThankYouSubscription)
        );
        store.set(visibleResultAtom, true);

        clearInterval(pollTimer);

        const orderPaid = res.transactionList.find((item) => item.payStatus === 1);

        // 先买后用场景：缓存 order_id，有效期1天（仅会员商品类型）
        const isSubscriptionProduct =
          orderPaid?.product?.productType === ProductType.RenewedSubscription ||
          orderPaid?.product?.productType === ProductType.NonRenewedSubscription;

        const module_id = getUrlParam('module_id');

        const isPayFirstUseLater =
          ['hzxf', 'ai_rmwrinkle', 'upscale', 'hbsj', 'tpbj', 'aippt'].includes(module_id) ||
          (module_id === 'home' && isFromCutout); // 抠图首页或者抠图简易工具

        if (isPayFirstUseLater && orderPaid?.transactionId && isSubscriptionProduct) {
          const expireAt = Date.now() + 24 * 60 * 60 * 1000;
          localStorage.setItem(
            'order_id',
            JSON.stringify({ value: orderPaid.transactionId, expireAt })
          );
        }

        // 普通会员是否配置买赠商品
        const promotionGiftForConsumable = orderPaid?.promotionGifts?.find(
          (item: any) => item.productType === ProductType.Consumable
        );
        store.set(payProductInfoAtom, orderPaid?.product || {});
        getMeidouCount();
        let params: any = {};
        if (getIncomeSource() === 'MTXX-WEB') {
          params = {
            class: orderPaid?.payChannel === 'weixin' ? 'wechat' : 'zfb',
            name_id: orderPaid?.product?.productId || '',
            money: (orderPaid as any)?.payAmount / 100, // 订单金额
            sku_type: getSkuType(),
            // 订单类型(1:非续期订阅 2:续期订阅 3:非消耗品 4:消耗品)
            order_type: orderPaid?.product?.productType || 0,
            touch_type: getUrlParam('touch_type') || 4,
            order_id: orderPaid?.transactionId, // 续期=合约号，非续期=订单号
          };

          // 团队版购买会员场景
          if (isTeamSubscription && curTab === TabType.SUB) {
            params['seat_num'] = (orderPaid?.product as any)?.quantity;
          }

          if ((orderPaid?.product?.productType as any) === 'CONSUMALBE') {
            // 买张数商品
            const commodityConfig = JSON.parse(orderPaid?.product.commodityConfig as string);
            params.buy_num = commodityConfig?.consumableCount?.count;
          } else if (promotionGiftForConsumable) {
            // 普通会员买赠商品
            params.buy_num = promotionGiftForConsumable?.commodityConfig?.count;
          }
        } else {
          params = {
            category: orderPaid?.payChannel === 'weixin' ? '微信' : '支付宝',
            product_id: orderPaid?.product?.productId || '',
            amount: ((orderPaid?.product?.price || 0) / 100).toString(),
          };
        }
        track({
          event_id: 'vip_paidprocess_success',
          event_name: '支付成功',
          params,
        });
      }
    } catch (e) {
      Sentry.captureException(
        `productTradeProgress error >>>>>>>>: ${typeof e === 'string' ? e : JSON.stringify(e)}`
      );
    }
  }, 5000);
};

const buildTradeParams = (productInfo: Product, transfer_id: string) => {
  const curTab = store.get(curTabAtom);
  const seats = store.get(seatsAtom);
  const selectedAddOnProduct = productInfo.addOn?.products?.find((item) => item.selected);
  const tradeProduct = selectedAddOnProduct || productInfo;
  const promotionInfo = tradeProduct?.promotions && tradeProduct.promotions[0];
  const params: TradeParams = {
    productId: tradeProduct.productId,
    transferId: transfer_id,
    promotionId: promotionInfo?.promotionId || '',
    tradeSessionId: productInfo.tradeSessionId,
  };

  if (isTeamSubscription) {
    params.quantity = curTab === TabType.SUB ? seats : 0;
    params.purchaseScene =
      purchaseScene === PurchaseScene.EXTRA ? TradePurchaseScene.Extra : TradePurchaseScene.Normal;
  }

  return params;
};

const createPayUrl = async (productInfo: Product, transfer_id: string) => {
  try {
    const params = buildTradeParams(productInfo, transfer_id);
    const res = await subInstance?.createCashierUrl(params);
    store.set(payUrlAtom, res?.url || '');
    pollTradeProgress(productInfo);
    store.set(loadingAtom, false);
  } catch (e: any) {
    store.set(loadingAtom, false);
    store.set(payUrlAtom, '');
    const msg = typeof e === 'string' ? e : e?.message;
    if (msg !== 'need login')
      Sentry.captureException(
        `createTradeUrl error >>>>>>>>> ${getIncomeSource()} : ${
          typeof e === 'string' ? e : JSON.stringify(e)
        }`
      );
  }
};

// 创建交易二维码
const createQrCode = async (productInfo: Product, transfer_id: string) => {
  try {
    const params = buildTradeParams(productInfo, transfer_id);
    const res = await subInstance?.createTradeQrCode(params);

    store.set(qrCodeUrlAtom, res?.url || '');

    pollTradeProgress(productInfo);

    store.set(loadingAtom, false);
  } catch (e: any) {
    store.set(loadingAtom, false);
    // 上报非未登录之外的异常错误
    const msg = typeof e === 'string' ? e : e?.message;
    if (msg !== 'need login')
      Sentry.captureException(
        `createTradeQrCode error >>>>>>>>> ${getIncomeSource()} : ${
          typeof e === 'string' ? e : JSON.stringify(e)
        }`
      );
  }
};

/**
 * 创建透传数据
 * @param productInfo
 */
export const trackSubscribeData = async () => {
  const productInfo = store.get(curProductAtom);
  store.set(loadingAtom, true);
  store.set(qrCodeUrlAtom, '');
  store.set(payUrlAtom, '');
  const createEntry = isMobileH5 ? createPayUrl : createQrCode;
  try {
    const data = await getTrackSubscribeDataParams();

    const res = await subInstance.getTransferId(data);

    if (res?.transferId) {
      transferId = res.transferId;
      createEntry(productInfo, res.transferId);
    }
  } catch (e) {
    createEntry(productInfo, '');
    Sentry.captureException(`trackSubscribeData error >>>>>>: ${e}`);
  }
};

/**
 * 清楚轮询的定时器
 */
export const clearPollTimer = () => {
  pollTimer && clearInterval(pollTimer);
};

/**
 * 刷新二维码
 */
export const handleRefreshQrCode = () => {
  // 清除上一次定时器
  clearPollTimer();

  const curProduct = store.get(curProductAtom);

  if (!curProduct) return;

  trackSubscribeData();
};

/**
 * 默认选中商品卡片
 */
export const initActiveProductIndex = () => {
  const products = store.get(productsAtom);
  const teamInfo = store.get(teamInfoAtom);
  const curTab = store.get(curTabAtom);
  const vipInfo = store.get(vipInfoAtom);

  let idx = products.findIndex((item) => item.preferred);
  idx = idx > -1 ? idx : products.length > 1 ? 1 : 0;

  if (curTab === TabType.SUB && getUrlParam('preferred') === 'month') {
    const monthIdx = products.findIndex(
      (item) =>
        item.productType === ProductType.RenewedSubscription &&
        item.subPeriod === SubscriptionPeriod.PerMonth
    );
    if (monthIdx > -1) idx = monthIdx;
  } else if (curTab === TabType.SUB && getUrlParam('preferred') === 'year') {
    const yearIdx = products.findIndex(
      (item) =>
        item.productType === ProductType.RenewedSubscription &&
        item.subPeriod === SubscriptionPeriod.PerYear
    );
    if (yearIdx > -1) idx = yearIdx;
  }

  // 个人版标准会员商品组：优先选中低价试用商品（promotion_type: 11 / 12）
  if (!isTeamSubscription && curTab === TabType.SUB) {
    const lowPriceTrialIdx = products.findIndex((item) => {
      const promotion = item?.promotions?.[0];
      const promotionType = Number(promotion?.promotionType);

      const promotionPrice = promotion?.promotionPrice?.price || 0;
      const productPrice = item.productPrice?.price || 0;

      // 是否使用过优惠
      const hasUsePromotion = promotionPrice !== productPrice;

      // 有推介资格的低价试用商品
      return (
        (promotionType === PromotionType.LowPriceTrialPromotion ||
          promotionType === PromotionType.LowPriceTrialPromotionOffers) &&
        hasUsePromotion
      );
    });
    if (lowPriceTrialIdx > -1) {
      idx = lowPriceTrialIdx;
    }
  }

  // 高级会员场景，仅首次加载时根据当前会员周期匹配默认选中商品
  if (curTab === TabType.PRO_SUB && vipInfo?.is_vip && isFirstSetActiveIndex) {
    const subPeriod = [SubscriptionPeriod.PerMonth, SubscriptionPeriod.PerQuarter].includes(
      vipInfo?.sub_period as SubscriptionPeriod
    )
      ? SubscriptionPeriod.PerMonth
      : vipInfo?.sub_period === SubscriptionPeriod.PerYear
      ? SubscriptionPeriod.PerYear
      : vipInfo.sub_period;
    const defaultIdx = products.findIndex(
      (item) => item.productType === ProductType.RenewedSubscription && item.subPeriod === subPeriod
    );
    if (defaultIdx > -1) {
      idx = defaultIdx;
    }
  }

  // 接口下发的 selected 商品 ID：若在当前商品列表中存在，则优先选中（新版 UI 不生效）
  const apiSelectedProductId = store.get(apiSelectedProductIdAtom);
  if (apiSelectedProductId) {
    const selectedIdx = products.findIndex((item) => item.productId === apiSelectedProductId);
    if (selectedIdx > -1) {
      idx = selectedIdx;
    }
  }

  // 团队版：URL 携带 team_level 时优先选中对应等级商品（product_id 仍可覆盖）
  const teamLevel = Number(getUrlParam('team_level'));
  if (isTeamSubscription && curTab === TabType.SUB && teamLevel) {
    const matched = products
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => Number((item as any)?.subCommodityBo?.level) === teamLevel);
    if (matched.length) {
      const preferred = matched.find(({ item }) => item.preferred);
      idx = (preferred ?? matched[0]).index;
    }
  }

  // 如果URL指定product_id，则选中对应的商品
  const productId = getUrlParam('product_id');
  if (productId) {
    idx = products.findIndex((item) => item.productId === productId);
  }

  store.set(activeIndexAtom, idx);

  isFirstSetActiveIndex = false;

  // 设置默认席位数为商品的最小席位数，团队版会员订阅
  if (isTeamSubscription && curTab === TabType.SUB) {
    if (purchaseScene === PurchaseScene.EXTRA) {
      // 加购场景，默认1
      store.set(seatsAtom, 1);
    } else {
      const current = products[idx] as GroupProduct;
      const lowerLimit = current?.seatInfo?.lowerLimit;
      // 当前团队绑定的成员数
      const userCount = teamInfo?.member_detail?.user_count || 0;
      const defaultSeats = +getUrlParam('seats');
      lowerLimit &&
        store.set(
          seatsAtom,
          current.isProbation ? lowerLimit : Math.max(lowerLimit, userCount, defaultSeats)
        );
    }
  }
};

export const initFn = async (callback?: () => void, isNewUI = false) => {
  isFirstSetActiveIndex = true;
  try {
    if (!isAppStorePay()) {
      // web端，非团队版
      if (!isTeamSubscription && getIncomeSource() === 'MTXX-WEB') {
        // 高级会员 tab 下根据实验决定美豆默认第一档还是第二档（initAbTest 已在 initPage 中完成）
        if (store.get(curTabAtom) === TabType.PRO_SUB && !isOnlyShowScanUI) {
          store.set(giftLevelAtom, checkIsDefaultSecondTierMeidou() ? 2 : 1);
        }
      }

      getProducts(isNewUI);
      // 获取美豆剩余详细
      if (subscribeType === SubscribeType.MEIDOU || checkIsNewUI()) {
        getMeidouCount();
      }
    } else {
      getProductMacList();
    }

    callback?.();
  } catch (e) {
    Sentry.captureException(`init error >>>>>>: ${e}`);
  }
};
